import { Link } from "react-router-dom";
import { LeafMark } from "../components/LeafMark";

export function Landing() {
  return (
    <div className="min-h-dvh bg-cream text-leaf-950">
      <TopNav />
      <Hero />
      <Stats />
      <ThreeSteps />
      <WhyFideliza />
      <ForCustomers />
      <FinalCta />
      <Footer />
    </div>
  );
}

function TopNav() {
  return (
    <nav className="sticky top-0 z-20 border-b border-leaf-100 bg-cream/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2">
          <LeafMark className="h-7 w-7 text-leaf-700" />
          <span className="font-display text-lg font-semibold tracking-tight">Fideliza</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/login"
            className="rounded-full px-3 py-1.5 text-sm font-medium text-leaf-700 hover:bg-leaf-50"
          >
            Iniciar sesion
          </Link>
          <Link
            to="/registrate"
            className="rounded-full bg-leaf-700 px-4 py-1.5 text-sm font-semibold text-white hover:bg-leaf-800"
          >
            Crear cuenta
          </Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <header className="mx-auto max-w-5xl px-5 pb-12 pt-10 sm:pt-16">
      <div className="flex flex-col items-center text-center">
        <span className="rounded-full bg-leaf-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-leaf-800">
          Hecho en Chile · Para comercios chilenos
        </span>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl md:text-6xl">
          Tus clientes vuelven
          <br className="hidden sm:block" />
          <span className="text-leaf-700"> 3 veces más.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base text-leaf-700 sm:text-lg">
          Convierte cada boleta en un cliente fiel.
          Sin tarjetitas, sin apps que descargar, sin sistema POS complicado.
          <strong> Listo en 5 minutos.</strong>
        </p>

        <div className="mt-7 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
          <Link
            to="/registrate"
            className="rounded-full bg-leaf-700 px-6 py-3 text-center text-base font-semibold text-white shadow-sm transition hover:bg-leaf-800"
          >
            Empezar gratis ahora
          </Link>
          <a
            href="#soy-cliente"
            className="rounded-full border border-leaf-300 bg-white px-6 py-3 text-center text-base font-medium text-leaf-800 transition hover:bg-leaf-50"
          >
            Soy cliente
          </a>
        </div>

        <p className="mt-3 text-xs text-leaf-600">
          Sin tarjeta de crédito. Sin contrato. 100 clientes gratis para siempre.
        </p>
      </div>
    </header>
  );
}

function Stats() {
  const items = [
    { value: "3×", label: "mas visitas al mes que sin programa" },
    { value: "100%", label: "de ventas trackeadas con QR + boleta" },
    { value: "24 h", label: "para tener tu programa en vivo" }
  ];
  return (
    <section className="border-y border-leaf-100 bg-white py-10">
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 px-5 sm:grid-cols-3">
        {items.map((s) => (
          <div key={s.label} className="text-center">
            <p className="font-display text-4xl font-semibold text-leaf-800 sm:text-5xl">{s.value}</p>
            <p className="mt-2 text-sm text-leaf-700">{s.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ThreeSteps() {
  const steps = [
    {
      n: 1,
      title: "Crea tu cuenta en 3 minutos",
      body:
        "RUT, nombre, color y logo. Tu QR brandeado queda listo al instante para imprimir y poner en la caja.",
      icon: (
        <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 11l-3 3-2-2" />
        </svg>
      )
    },
    {
      n: 2,
      title: "Imprime tu cartel",
      body:
        "Descarga el poster en formato A4, A6 o sticker (300 DPI). Con tu logo y color. Lo pones en la caja y listo, sin imprenta especial.",
      icon: (
        <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="16" y="16" width="5" height="5" rx="1" />
        </svg>
      )
    },
    {
      n: 3,
      title: "Tus clientes vuelven",
      body:
        "Muestran su QR personal al cajero y la venta queda registrada al toque. ¿No alcanzaste a estar en caja? Suben la foto de su boleta y la IA la valida sola.",
      icon: (
        <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2l2.39 4.84 5.34.78-3.86 3.76.91 5.32L12 14.18l-4.78 2.52.91-5.32L4.27 7.62l5.34-.78L12 2z" />
        </svg>
      )
    }
  ];

  return (
    <section className="border-y border-leaf-100 bg-white py-14">
      <div className="mx-auto max-w-6xl px-5">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Así de simple</p>
          <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
            De cero a fidelizando en 5 minutos
          </h2>
          <p className="mt-2 max-w-xl mx-auto text-sm text-leaf-700">
            Sin instalar software, sin contratar técnicos, sin cambiar tu caja registradora.
          </p>
        </header>

        <div className="mt-10 grid gap-5 sm:grid-cols-3">
          {steps.map((s) => (
            <article
              key={s.n}
              className="relative rounded-3xl border border-leaf-100 bg-cream p-6"
            >
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-leaf-700 font-display text-lg font-semibold text-white">
                  {s.n}
                </span>
                <span className="text-leaf-700">{s.icon}</span>
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-leaf-700">{s.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyFideliza() {
  const bullets = [
    {
      title: "Loyalty con QR",
      body:
        "Dos formas de sumar visitas, una sola app. El cliente muestra su QR personal al cajero y la venta queda registrada al instante. Si no alcanzaste a estar en caja, sube la foto de su boleta y el sistema la valida solo. Tú defines cuántas visitas valen un reward y qué das. Cero tarjetas de cartón, cero sellos perdidos."
    },
    {
      title: "Tus clientes te venden gratis",
      body:
        "Tus clientes con seguidores postulan en 30 segundos. Los apruebas con un click y reciben su código personal + kit listo para WhatsApp, Instagram y TikTok. Cada amigo que llega con su código se lleva descuento y tu embajador gana comisión automática. Pones un tope mensual si quieres."
    },
    {
      title: "Antifraude automatico",
      body:
        "El sistema lee cada boleta (RUT, productos, monto) y rechaza al toque las que no son tuyas o ya se usaron. Los descuentos solo se aplican una vez por cliente cada 24 h. Los cajeros pueden deshacer una venta dentro de 5 min; después solo el admin con motivo registrado. Cada peso de comisión validado."
    },
    {
      title: "Decisiones con datos, no con feeling",
      body:
        "Dashboard en vivo desde tu celular. Ingresos del día, semana y mes. Ticket promedio. Nuevos clientes esta semana. Retención a 30 días. Top embajadores ranqueados. Ventas por cajero filtrables por fecha. Ventas a revisar (disputadas, devoluciones) con un solo tap."
    },
    {
      title: "Equipo conectado en 1 minuto",
      body:
        "Invitas a tu cajero por WhatsApp con un link que expira en 24 h. Cada cajero ve solo sus ventas del día, deshace lo suyo si se equivocó, y tú como dueño revisas todo desde el filtro 'Por revisar'. Sin capacitar, sin POS, sin instalar."
    },
    {
      title: "Sin riesgo, sin contrato",
      body:
        "Empiezas gratis hasta 100 clientes. Cancelas cuando quieras. Te llevas tu base de datos. Cero permanencia."
    }
  ];

  return (
    <section className="py-14">
      <div className="mx-auto max-w-6xl px-5">
        <header className="text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Por que Fideliza</p>
          <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
            Mas que un loyalty: un sistema de crecimiento
          </h2>
        </header>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {bullets.map((b) => (
            <article
              key={b.title}
              className="rounded-2xl border border-leaf-100 bg-white p-5"
            >
              <div className="flex items-start gap-3">
                <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-leaf-100 text-leaf-700">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </span>
                <div>
                  <h3 className="font-display text-base font-semibold">{b.title}</h3>
                  <p className="mt-1 text-sm text-leaf-700">{b.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForCustomers() {
  return (
    <section id="soy-cliente" className="border-y border-leaf-100 bg-leaf-50/40 py-14">
      <div className="mx-auto flex max-w-4xl flex-col items-center px-5 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-leaf-600">Eres cliente</p>
        <h2 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
          Que tu fidelidad te dé algo a cambio
        </h2>
        <p className="mt-3 max-w-2xl text-leaf-700">
          Cada vez que compres en un local que use Fideliza, muestras tu QR al cajero o subes tu boleta.
          Acumulas visitas y ganas premios gratis. Y si te encanta el lugar,
          <strong> puedes ganar plata recomendándolo</strong> a tus amigos.
        </p>

        <div className="mt-7 grid w-full max-w-3xl gap-3 sm:grid-cols-3">
          <Step
            icon="📱"
            title="Activa tu QR"
            body="Escanea el QR del local, activa tu codigo y tu QR personal queda listo."
          />
          <Step
            icon="☕"
            title="Muestralo al pagar"
            body="El cajero lo escanea, descuento aplicado, ticket sumado al toque."
          />
          <Step
            icon="🎁"
            title="Gana tu reward"
            body="Al juntar las visitas que pida el local, retiralo en caja."
          />
        </div>

        <div className="mt-7 rounded-2xl border border-leaf-200 bg-white px-5 py-4 text-sm text-leaf-800">
          <strong>Bonus:</strong> postula a embajador desde tu cuenta y gana comisión
          por cada venta hecha con tu codigo.
        </div>

        <Link
          to="/login"
          className="mt-7 rounded-full border border-leaf-300 bg-white px-6 py-3 text-base font-medium text-leaf-800 transition hover:bg-leaf-50"
        >
          Ya tengo cuenta · Iniciar sesión
        </Link>
      </div>
    </section>
  );
}

function Step({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-leaf-100 bg-white p-4 text-center">
      <div className="text-2xl">{icon}</div>
      <p className="mt-2 font-display text-base font-semibold">{title}</p>
      <p className="mt-1 text-xs text-leaf-700">{body}</p>
    </div>
  );
}

function FinalCta() {
  return (
    <section className="py-14">
      <div className="mx-auto max-w-3xl px-5">
        <div className="rounded-3xl bg-leaf-700 px-6 py-12 text-center text-white shadow-xl">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl">
            Tu próximo cliente fiel está esperando.
          </h2>
          <p className="mt-3 text-leaf-100">
            5 minutos para empezar. Gratis hasta 100 clientes. Sin tarjeta de crédito.
          </p>
          <div className="mt-7 flex flex-col items-stretch gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/registrate"
              className="rounded-full bg-white px-6 py-3 text-center text-base font-semibold text-leaf-800 transition hover:bg-leaf-50"
            >
              Crear cuenta gratis
            </Link>
            <a
              href="mailto:hola@fidelizapp.cl?subject=Quiero%20saber%20mas%20de%20Fideliza"
              className="rounded-full border border-white/60 px-6 py-3 text-center text-base font-medium text-white transition hover:bg-white/10"
            >
              Hablar con alguien
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-leaf-100 bg-cream py-8">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 text-sm text-leaf-700 sm:flex-row">
        <div className="flex items-center gap-2">
          <LeafMark className="h-5 w-5 text-leaf-700" />
          <span className="font-display font-semibold">Fideliza</span>
          <span className="text-leaf-500">· hola@fidelizapp.cl</span>
        </div>
        <div className="flex items-center gap-4">
          <a href="https://instagram.com/fidelizapp" target="_blank" rel="noreferrer" className="hover:text-leaf-900">
            Instagram
          </a>
          <Link to="/login" className="hover:text-leaf-900">Iniciar sesion</Link>
          <Link to="/registrate" className="hover:text-leaf-900">Crear cuenta</Link>
        </div>
      </div>
      <p className="mt-3 text-center text-xs text-leaf-500">
        © {new Date().getFullYear()} Fideliza · Hecho en Chile
      </p>
    </footer>
  );
}
